import React, { useState, useEffect } from 'react';
import api from '../utils/api';

interface PrivacyData {
  contactEmail: string;
  responsiblePerson: string;
  lastUpdated: string;
}

const Privacy: React.FC = () => {
  const [privacy, setPrivacy] = useState<PrivacyData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPrivacy();
  }, []);

  const fetchPrivacy = async () => {
    try {
      const response = await api.get('/legal/privacy');
      setPrivacy(response.data.data);
    } catch (error) {
      console.error('Fehler beim Laden der Datenschutzerklärung:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="loading">Lädt...</div>;
  }

  return (
    <div className="container">
      <div style={{
        background: 'linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%)',
        borderRadius: '16px',
        padding: '30px',
        marginBottom: '30px',
        color: 'white',
        boxShadow: '0 8px 20px rgba(15, 32, 39, 0.3)'
      }}>
        <h1 style={{ margin: 0 }}>Datenschutzerklärung</h1>
        {privacy && (
          <p style={{ margin: '10px 0 0 0', opacity: 0.9, fontSize: '14px' }}>
            Stand: {new Date(privacy.lastUpdated).toLocaleDateString('de-DE')}
          </p>
        )}
      </div>

      <div className="card" style={{ maxWidth: '800px', margin: '0 auto' }}>
        {privacy && (
          <div style={{ lineHeight: '1.8' }}>
            <h2 style={{ color: '#2c5364', marginBottom: '20px' }}>1. Datenschutz auf einen Blick</h2>
            
            <h3 style={{ color: '#2c5364', marginTop: '25px', marginBottom: '15px' }}>Allgemeine Hinweise</h3>
            <p style={{ textAlign: 'justify' }}>
              Diese Datenschutzerklärung informiert Sie über die Art, den Umfang und Zweck der 
              Verarbeitung von personenbezogenen Daten innerhalb dieser Anwendung.
            </p>

            <h2 style={{ color: '#2c5364', marginTop: '35px', marginBottom: '20px' }}>2. Verantwortliche Stelle</h2>
            <p>
              Verantwortlich für die Datenverarbeitung ist:<br />
              <strong>{privacy.responsiblePerson}</strong><br />
              E-Mail: <a href={`mailto:${privacy.contactEmail}`} style={{ color: '#2c5364' }}>{privacy.contactEmail}</a>
            </p>

            <h2 style={{ color: '#2c5364', marginTop: '35px', marginBottom: '20px' }}>3. Datenerfassung</h2>
            
            <h3 style={{ color: '#2c5364', marginTop: '25px', marginBottom: '15px' }}>Welche Daten werden erfasst?</h3>
            <p style={{ textAlign: 'justify' }}>
              Wir erfassen folgende Daten:
            </p>
            <ul style={{ lineHeight: '1.8' }}>
              <li><strong>Kontodaten:</strong> Name, E-Mail-Adresse, Passwort (verschlüsselt)</li>
              <li><strong>Finanzdaten:</strong> Konten, Transaktionen, Schulden, Abonnements</li>
              <li><strong>Technische Daten:</strong> IP-Adresse, Browser-Informationen, Zeitstempel</li>
            </ul>

            <h3 style={{ color: '#2c5364', marginTop: '25px', marginBottom: '15px' }}>Wie werden Daten erfasst?</h3>
            <p style={{ textAlign: 'justify' }}>
              Ihre Daten werden erfasst, wenn Sie diese selbst in der Anwendung eingeben. 
              Dies umfasst alle Finanztransaktionen und Kontoinformationen, die Sie freiwillig bereitstellen.
            </p>

            <h3 style={{ color: '#2c5364', marginTop: '25px', marginBottom: '15px' }}>Belegscanner (OCR)</h3>
            <p style={{ textAlign: 'justify' }}>
              Diese Anwendung bietet die Möglichkeit, Belege per Kamera zu scannen und automatisch auszulesen (OCR - Optical Character Recognition).
            </p>
            <ul style={{ lineHeight: '1.8' }}>
              <li><strong>Lokale Verarbeitung:</strong> Die OCR-Verarbeitung erfolgt vollständig lokal in Ihrem Browser mittels Tesseract.js</li>
              <li><strong>Keine Server-Übertragung:</strong> Ihre fotografierten Belege werden NICHT an unsere Server oder Drittanbieter übertragen</li>
              <li><strong>Keine Speicherung:</strong> Die Bilder werden nach der Texterkennung nicht gespeichert. Nur die extrahierten Textdaten (Betrag, Händler, Datum) werden in Ihrer Transaktion gespeichert</li>
              <li><strong>Freiwillige Nutzung:</strong> Die Nutzung des Belegscanners ist optional. Sie können Transaktionen jederzeit manuell eingeben</li>
            </ul>

            <h2 style={{ color: '#2c5364', marginTop: '35px', marginBottom: '20px' }}>4. Datenspeicherung und Verschlüsselung</h2>
            <p style={{ textAlign: 'justify' }}>
              <strong>Lokale Speicherung:</strong> Alle Ihre Finanzdaten werden ausschließlich in Ihrer 
              persönlichen Datenbank gespeichert. Es erfolgt keine Weitergabe an Dritte.
            </p>
            <p style={{ textAlign: 'justify' }}>
              <strong>Verschlüsselung:</strong> Sensible Daten wie Kontostände und Transaktionen werden 
              mit AES-256-GCM verschlüsselt. Passwörter werden mit bcrypt gehasht und sind nicht im 
              Klartext einsehbar.
            </p>
            <p style={{ textAlign: 'justify' }}>
              <strong>SSL/TLS:</strong> Die Übertragung Ihrer Daten erfolgt verschlüsselt über HTTPS.
            </p>

            <h2 style={{ color: '#2c5364', marginTop: '35px', marginBottom: '20px' }}>5. Zweck der Datenverarbeitung</h2>
            <p style={{ textAlign: 'justify' }}>
              Ihre Daten werden ausschließlich zur Bereitstellung der Funktionalität dieser Anwendung 
              verwendet:
            </p>
            <ul style={{ lineHeight: '1.8' }}>
              <li>Verwaltung Ihrer Konten und Finanzen</li>
              <li>Berechnung von Statistiken und Übersichten</li>
              <li>Authentifizierung und Zugriffskontrolle</li>
              <li>Zwei-Faktor-Authentifizierung via E-Mail</li>
            </ul>

            <h2 style={{ color: '#2c5364', marginTop: '35px', marginBottom: '20px' }}>6. Cookies und Speicherung</h2>
            <p style={{ textAlign: 'justify' }}>
              Diese Anwendung verwendet localStorage zur Speicherung von Authentifizierungs-Tokens 
              in Ihrem Browser. Diese werden benötigt, um Sie angemeldet zu halten.
            </p>

            <h2 style={{ color: '#2c5364', marginTop: '35px', marginBottom: '20px' }}>7. Ihre Rechte</h2>
            <p style={{ textAlign: 'justify' }}>Sie haben jederzeit das Recht auf:</p>
            <ul style={{ lineHeight: '1.8' }}>
              <li><strong>Auskunft</strong> über Ihre gespeicherten Daten</li>
              <li><strong>Berichtigung</strong> unrichtiger Daten</li>
              <li><strong>Löschung</strong> Ihrer Daten (Account löschen)</li>
              <li><strong>Einschränkung</strong> der Datenverarbeitung</li>
              <li><strong>Datenübertragbarkeit</strong> (Export Ihrer Daten)</li>
              <li><strong>Widerspruch</strong> gegen die Datenverarbeitung</li>
            </ul>

            <h2 style={{ color: '#2c5364', marginTop: '35px', marginBottom: '20px' }}>8. Datenweitergabe</h2>
            <p style={{ textAlign: 'justify' }}>
              <strong>Keine Weitergabe an Dritte:</strong> Ihre Daten werden nicht an Dritte weitergegeben, 
              verkauft oder anderweitig kommerziell genutzt. Die Daten verbleiben ausschließlich in Ihrer 
              persönlichen Instanz dieser Anwendung.
            </p>

            <h2 style={{ color: '#2c5364', marginTop: '35px', marginBottom: '20px' }}>9. Datensicherheit</h2>
            <p style={{ textAlign: 'justify' }}>
              Wir setzen technische und organisatorische Sicherheitsmaßnahmen ein, um Ihre Daten 
              gegen zufällige oder vorsätzliche Manipulationen, Verlust, Zerstörung oder den Zugriff 
              unberechtigter Personen zu schützen.
            </p>
            <p style={{ textAlign: 'justify', marginTop: '15px' }}>
              <strong>Wichtiger Hinweis:</strong> Trotz aller Sicherheitsmaßnahmen kann keine absolute 
              Sicherheit garantiert werden. Wir empfehlen regelmäßige Backups Ihrer Daten. 
              <strong> Eine Haftung für Datenverlust ist ausgeschlossen.</strong>
            </p>

            <h2 style={{ color: '#2c5364', marginTop: '35px', marginBottom: '20px' }}>10. Haftungsausschluss</h2>
            <div style={{
              background: 'rgba(240, 147, 251, 0.1)',
              padding: '20px',
              borderRadius: '12px',
              border: '2px solid rgba(240, 147, 251, 0.3)',
              marginTop: '20px'
            }}>
              <p style={{ textAlign: 'justify', margin: 0 }}>
                <strong>Keine Haftung für Berechnungen:</strong> Diese Anwendung dient der privaten 
                Finanzverwaltung. Alle Berechnungen erfolgen automatisch, jedoch ohne Gewähr auf 
                Richtigkeit. <strong>Eine Haftung für fehlerhafte Berechnungen, Datenverlust oder 
                daraus resultierende finanzielle Schäden wird ausdrücklich ausgeschlossen.</strong>
              </p>
              <p style={{ textAlign: 'justify', marginTop: '15px', marginBottom: 0 }}>
                Die Nutzung dieser Anwendung erfolgt auf eigenes Risiko. Es wird empfohlen, wichtige 
                Berechnungen manuell zu überprüfen und regelmäßig Backups anzulegen.
              </p>
            </div>

            <h2 style={{ color: '#2c5364', marginTop: '35px', marginBottom: '20px' }}>11. Änderungen der Datenschutzerklärung</h2>
            <p style={{ textAlign: 'justify' }}>
              Wir behalten uns vor, diese Datenschutzerklärung anzupassen, damit sie stets den 
              aktuellen rechtlichen Anforderungen entspricht oder um Änderungen unserer Leistungen 
              umzusetzen. Für Ihren erneuten Besuch gilt dann die neue Datenschutzerklärung.
            </p>

            <h2 style={{ color: '#2c5364', marginTop: '35px', marginBottom: '20px' }}>12. Kontakt</h2>
            <p>
              Bei Fragen zum Datenschutz wenden Sie sich bitte an:<br />
              <a href={`mailto:${privacy.contactEmail}`} style={{ color: '#2c5364', fontWeight: 'bold' }}>
                {privacy.contactEmail}
              </a>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Privacy;
